/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React, {
    useContext, useEffect, useRef, useState,
} from 'react';
import {
    object, string, shape, arrayOf, number, func, bool, array, oneOfType,
} from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from '@material-ui/core';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ProductImageUI from '../../../ProductImageUI';
import style from './BYOProduct.module.css';
import ScrollbarContainer from '../../../../../GraphqlCategoryPage/Partials/ScrollbarContainer/ScrollbarContainer';
import BYOProductContentRight from './BYOProductContentRight';
import DesktopMobile from '../../../../../../../helpers/DesktopMobile/DesktopMobile';
import ScreenAdjustableOverlay from '../../UiHelper/ScreenAdjustableOverlay';
import { getSelectedBYOItems } from '../../../../../../../../state/ducks/App/App-Selectors';
import { setSelectedBYOItems } from '../../../../../../../../state/ducks/App/App-Actions';
import { trackEvent } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import { getBaseCode } from '../../../../../../../helpers/tracking/product/dataLayerProductHelpers';
import { CrossMerch } from '../../../../../CrossMerchContainer/CrossMerchContainer';
import FloatingProgressBar from './FloatingProgressBar';
import { getCurrentPageType } from '../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Selectors';
import YotpoClickEvent from '../../../StandardProductsPage/YotpoClickEvent';

const BYOProductPage = ({
    addonContainer,
    data,
    selectedSku,
    skuData,
    content,
    presentation_family,
    handleSkuChange,
    location,
    brand,
    productBaseContent,
    handleMiniCartModalClick,
    nonCountableKey,
    brandId,
    product,
    cartError,
    hasWine,
    handleMobileAgeVerification,
    categoryData,
    deliverySLA,
    selectedAddons,
    showErrorMessage,
    hideMenu,
    useGuided,
    forcedIndex,
    hasAddons,
    featureFlags,
    url,
    setSelectedComponents,
}) => {
    useStyles(style);

    const { featureFlags: flags = {} } = useContext(CrossMerch);
    const cyoPdpYotpoEnabled = Boolean(flags?.['is-pdp-yotpo-enabled']);
    const multiProgressBarEnabled = Boolean(flags?.['is-byo-mutiprogress-bar-enabled']);
    const { isByoStaticProgressBarEnabled } = useFlags();
    const elementRef = useRef(null);
    const [height, setElementDimensions] = useState('auto');
    const dispatch = useDispatch();
    const selectedItems = useSelector(getSelectedBYOItems);
    const [showProgressBar, setShowProgressBar] = useState(true);
    const [maxSelectableForSku, setMaxSelectableForSku] = useState(0);
    const directStep = selectedItems?.step || 1;
    const [activeStep, setActiveStep] = useState(directStep);
    const pageType = useSelector(getCurrentPageType);
    const { clickableDivRef } = YotpoClickEvent(dispatch);

    const isMobile = useMediaQuery('(max-width:1024px)');
    useEffect(() => {
        dispatch(trackEvent({
            ga_event_name: 'byo_impression',
            eventCategory: 'BYO',
            eventAction: 'Test Impression',
            eventLabel: `Variant - ${data.partNumber} - ${data.name}`,
            nonInteraction: '1',
        }));
        if (typeof isByoStaticProgressBarEnabled !== 'undefined' && !window.wuByoEventFired) {
            dispatch(trackEvent({
                eventCategory: 'Experiments',
                eventAction: 'BYO Static Progress Bar',
                eventLabel: isByoStaticProgressBarEnabled ? 'Variant' : 'Control',
                nonInteraction: true,
                queued: !!(typeof window !== 'undefined' && !window.utag?.data),
            }));
            window.wuByoEventFired = true;
        }
        return () => {
            window.wuByoEventFired = false;
        };
    }, []);
    useEffect(() => {
        if (!selectedItems?.selectedSku || selectedItems?.selectedSku === 'undefined') {
            dispatch(setSelectedBYOItems({
                ...selectedItems,
                selectedSku,
                step: 1,
            }));
        }
    }, [selectedItems]);

    const getHeight = () => {
        if (elementRef.current) {
            const offsetHeight = elementRef.current.offsetHeight;
            setElementDimensions(offsetHeight);
        }
    };
    useEffect(() => {
        // Here, the component has rendered, and you can access the height.
        setTimeout(() => {
            getHeight();
        }, 2000);
    }, [elementRef]);
    const getMaxSelectable = () => {
        let num = 0;
        // Because the maxQuantity does not take into account the
        // non-countable items or bands, we cannot here either for consistency
        if (!selectedItems?.selectedSku?.cyo?.maxQuantity) {
            selectedItems?.selectedSku?.cyo?.cyoGroup?.forEach((group) => {
                if (group.type !== nonCountableKey && group.type !== 'cyoBands') {
                    num += group.numberofUnitsRequiredForComponent;
                }
            });
        } else {
            num = selectedItems?.selectedSku?.cyo?.maxQuantity;
        }
        return num;
    };

    let steps = [
        {
            label: 'Gift Size',
            title: 'Gift Size',
            key: 'giftSize',
        },
        {
            label: 'Pick Items',
            title: `Pick ${getMaxSelectable()} Items`,
            key: 'addItem',
        },
        {
            label: 'Decoration',
            title: 'Add a Custom Touch (Optional)',
            key: 'decoration',
        },
        {
            label: 'Review',
            title: 'Review',
            key: 'review',
        },
    ];
    if (skuData?.length === 1) {
        // Find the index of "Gift Size" step
        const giftSizeIndex = steps.findIndex((step) => step.key === 'giftSize');
        // If "Gift Size" step exists, remove it
        if (giftSizeIndex !== -1) {
            steps.splice(giftSizeIndex, 1); // Remove "Gift Size" step
        }
    }

    const selectSku = selectedItems?.selectedSku || selectedSku;
    const visibleGroups = selectSku?.cyo?.cyoGroup?.filter((g) => g.type !== 'cyoNonCountable');
    const cyoBand = visibleGroups?.findIndex((item) => item.type === 'cyoBands');
    const maxGroupNumber = visibleGroups?.filter((item) => item?.numberofUnitsRequiredForComponent === getMaxSelectable());
    if (cyoBand === -1) {
        steps = steps.filter((item) => item.key !== 'decoration');
    }

    useEffect(() => {
        setActiveStep(directStep);
    }, [directStep]);

    const scrollStep = () => {
        if (typeof document !== 'undefined') {
            setTimeout(() => {
                const nextStepElement = document.getElementById('progressStep');
                if (nextStepElement) {
                    const topPosition = nextStepElement.getBoundingClientRect().top + window.scrollY - 120;
                    window.scrollTo({
                        top: topPosition,
                        behavior: 'smooth',
                    });
                }
            }, 100);
        }
    };

    const desktopScroll = () => {
        if (typeof document !== 'undefined') {
            setTimeout(() => {
                const nextStepElement = document.getElementById('progressStep');
                if (nextStepElement) {
                    const topPosition = nextStepElement.getBoundingClientRect().top + window.scrollY - 10;
                    window.scrollTo({
                        top: topPosition,
                        behavior: 'smooth',
                    });
                }
            }, 100);
        }
    };

    const nextStep = (step, isScroll = true) => {
        setActiveStep(activeStep + 1);
        const updateSection = {
            ...selectedItems,
            step: activeStep + 1,
        };
        const clickedStep = steps?.[step] || {};
        dispatch(setSelectedBYOItems(updateSection));
        if (isMobile && isScroll) { scrollStep(step + 1); } else if (isScroll) { desktopScroll(step + 1, clickedStep); }
    };

    const prevStep = (key, notFromButtons = true) => {
        setActiveStep(key);
        const updateSection = {
            ...selectedItems,
            step: key,
        };
        const clickedStep = steps?.[key - 1] || {};
        if (notFromButtons) {
            dispatch(trackEvent({
                ga_event_name: 'byo_interaction',
                eventCategory: 'BYO',
                eventAction: 'BYO edit click',
                eventLabel: clickedStep?.label || '',

                interaction_type: 'select_item',
                byo_step: '',
                byo_item_name: clickedStep?.label,
                byo_item_variant: clickedStep?.label,
                byo_item_quantity: '',

            }));
        }
        dispatch(setSelectedBYOItems(updateSection));
        if (isMobile) { scrollStep(key); } else { desktopScroll(key); }
    };

    const handleItemRemove = () => {
        const { addItems, ...updatedData } = selectedItems;
        dispatch(setSelectedBYOItems(updatedData));
    };

    const directSteps = skuData?.length === 1 ? 1 : 2;
    const disabledButton = selectedItems?.addItems?.totalProductSelected !== getMaxSelectable();
    const floatingProgressBarEnabled = showProgressBar && activeStep === directSteps && (maxGroupNumber?.length > 0 || !multiProgressBarEnabled) && isByoStaticProgressBarEnabled;
    const productImageRender = () => (
        <div className={style.contentLeft} ref={elementRef}>
            <ProductImageUI
                selectedSku={selectSku}
                location={location}
                presentation_family={presentation_family}
                product={data}
                brand={brand}
                content={content}
                smartGiftLetThemKnowSection={{}}
                productBaseContent={productBaseContent}
                productName={data.name}
                productNameDisplay={data.name}
                isSmartGiftEnabled={false}
            />
        </div>
    );
    const productDetailRender = () => (
        <BYOProductContentRight
            cyoData={selectedItems?.selectedSku?.cyo || {}}
            handleMiniCartModalClick={handleMiniCartModalClick}
            nonCountableKey={nonCountableKey}
            selectedItemId={selectedItems?.selectedSku?.id || ''}
            brandId={brandId}
            product={product}
            cartError={cartError}
            hasWine={hasWine}
            handleMobileAgeVerification={handleMobileAgeVerification}
            categoryData={categoryData}
            deliverySLA={deliverySLA}
            selectedAddons={selectedAddons}
            showErrorMessage={showErrorMessage}
            hideMenu={hideMenu}
            useGuided={useGuided}
            forcedIndex={forcedIndex}
            hasAddons={hasAddons}
            featureFlags={featureFlags}
            url={url}
            setSelectedComponents={setSelectedComponents}
            addonContainer={addonContainer}
            handleSkuChange={handleSkuChange}
            skuData={skuData}
            content={content}
            data={data}
            selectedSku={selectedItems?.selectedSku}
            selectedItems={selectedItems}
            getMaxSelectable={getMaxSelectable}
            setMaxSelectableForSku={setMaxSelectableForSku}
            maxSelectableForSku={maxSelectableForSku}
            setShowProgressBar={setShowProgressBar}
            activeStep={activeStep}
            steps={steps}
            nextStep={nextStep}
            prevStep={prevStep}
            removeItems={handleItemRemove}
            directSteps={directSteps}
        />
    );
    const dividerContainer = () => (
        <div className={style.component}>
            <div className={style.rectangle}>
                <span className={style.dividerLine} />
            </div>
        </div>
    );
    const deskTopView = () => (
        <div data-testid="deskTopView" className={style.mainContainer}>
            <div
                className={style.contentTop}
            >
                <ScrollbarContainer
                    contentContainerId="contentRight"
                    isFoodBrand
                    type="productBase"
                >
                    {productImageRender()}
                </ScrollbarContainer>
                <div className={style.contentRight} id="contentRight">
                    {productDetailRender()}
                </div>
            </div>
            {floatingProgressBarEnabled && (
                <FloatingProgressBar
                    totalProductSelected={selectedItems?.addItems?.totalProductSelected}
                    activeStep={activeStep}
                    handleItemRemove={handleItemRemove}
                    desktop
                    selectedSku={selectedSku}
                    nextStep={nextStep}
                    disabledButton={disabledButton}
                    maxSelectableForSku={maxSelectableForSku}
                />
            )}
        </div>
    );
    const mobileView = () => (
        <div className={style.mobileContainer}>
            <ScreenAdjustableOverlay
                topContainer={productImageRender()}
                bottomContainer={productDetailRender()}
                dividerContainer={dividerContainer()}
                minHeight={55}
                defaultHeight={height}
            />
            {floatingProgressBarEnabled && (
                <FloatingProgressBar
                    totalProductSelected={selectedItems?.addItems?.totalProductSelected}
                    activeStep={activeStep}
                    handleItemRemove={handleItemRemove}
                    selectedSku={selectedSku}
                    nextStep={nextStep}
                    disabledButton={disabledButton}
                    maxSelectableForSku={maxSelectableForSku}
                />
            )}
        </div>
    );

    const showAggregatedRating = () => (featureFlags['is-show-product-reviews-aggregated-star-rating'] ? 'gold-stars' : 'pr-Review-Image-Display-Disclaimer pr-reviewdisplay-no-aggregated-star-rating black-stars');
    const showRatingDistribution = () => (featureFlags['is-show-product-reviews-star-distribution'] ? '' : style.hidePRStarDistribution);

    return (
        <>
            <DesktopMobile
                desktop={deskTopView}
                mobile={mobileView}
                breakpoint={767}
            />
            {featureFlags['is-pdp-reviews-enabled'] && featureFlags['is-power-review-enabled']
                && (
                    <div style={{ margin: '0 10px' }}>
                        <div
                            id="pr-reviewdisplay"
                            className={`${style.prReviewDisplay} ${style.prReviewDisplayHelfulActionGroup} pr-Review-Display-Snapshot-Mobile ${showAggregatedRating()} ${showRatingDistribution()}`}
                        />
                        <div
                            id="pr-questiondisplay"
                            className={`${style.prQuestionDisplayForm} ${style.prQandIcon}`}
                        />
                    </div>
                )}
            {featureFlags['is-pdp-reviews-enabled'] && cyoPdpYotpoEnabled
                && (
                    <div className={style.yotpoReviewContainer} id="yotpo">
                        <div
                            ref={clickableDivRef}
                            className="yotpo yotpo-main-widget"
                            data-product-id={getBaseCode(product.partNumber)}
                            // data-price={yotpoConfig?.price?.prices?.[1]?.value}
                            data-currency="USD"
                            data-name={product.name}
                            data-url={url}
                            data-image-url={`${product.image?.path}${product.image?.name}x.jpg`}
                        />
                    </div>
                )}
        </>
    );
};
BYOProductPage.propTypes = {
    addonContainer: func,
    data: shape({
        name: string.isRequired,
    }).isRequired,
    selectedSku: array,
    skuData: array.isRequired,
    brand: shape({
        domain: string.isRequired,
    }).isRequired,
    content: shape({
        entries: arrayOf(shape({
            sku_section: object.isRequired,
            url: string.isRequired,
        })),
    }).isRequired,
    presentation_family: string.isRequired,
    handleSkuChange: func.isRequired,
    location: object.isRequired,
    productBaseContent: object,
    url: string,
    brandId: string.isRequired,
    handleMiniCartModalClick: func.isRequired,
    product: object.isRequired,
    hasWine: bool,
    cartError: string.isRequired,
    handleMobileAgeVerification: func,
    nonCountableKey: string.isRequired,
    featureFlags: object.isRequired,
    categoryData: shape({
        categoryId: string,
        categoryName: string,
        categoryPath: string,
    }),
    deliverySLA: string.isRequired,
    selectedAddons: array.isRequired,
    showErrorMessage: func.isRequired,
    hideMenu: bool,
    useGuided: bool,
    forcedIndex: oneOfType([number, bool]),
    hasAddons: bool,
    setSelectedComponents: func,
};
BYOProductPage.defaultProps = {
    addonContainer: () => null,
    url: '',
    productBaseContent: {},
    hasWine: false,
    handleMobileAgeVerification: () => { },
    categoryData: {},
    hideMenu: false,
    useGuided: true, // Change if needed for testing
    forcedIndex: false,
    hasAddons: false,
    setSelectedComponents: () => { },
    selectedSku: [],
};
export default BYOProductPage;
